/**
 * Two-factor overview - replaces allauth's mfa/index.html
 * Reached from Profile Settings -> Change Password.
 */
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import {
  MFA_URLS,
  MfaBackLink,
  MfaContent,
  MfaMessages,
  MfaText,
  PrimaryButton,
  SecondaryButton,
} from "../components/MfaParts";
import { useAuthContext } from "../hooks/useAuthContext";
import { gettext, interpolate } from "@services/i18n";

const TITLE = gettext("Two-factor authentication");
const SUBTITLE = gettext(
  "Protect your account with a code from an authenticator app in addition to your password.",
);
const AUTHENTICATOR_APP = gettext("Authenticator app");
const ACTIVE = gettext("Active");
const NOT_ACTIVE = gettext("Not active");
const TOTP_ACTIVE_TEXT = gettext(
  "When you sign in, you enter a code from your authenticator app after your password.",
);
const TOTP_INACTIVE_TEXT = gettext(
  "Use an app such as Microsoft Authenticator or Google Authenticator to generate sign-in codes.",
);
const ACTIVATE = gettext("Activate");
const DEACTIVATE = gettext("Deactivate");
const RECOVERY_CODES = gettext("Recovery codes");
const RECOVERY_CODES_TEXT = gettext(
  "If you lose access to your authenticator app, you can sign in with a recovery code instead.",
);
const RECOVERY_CODES_COUNT = gettext("%s of %s recovery codes are unused.");
const NO_RECOVERY_CODES = gettext("No recovery codes set up.");
const VIEW_CODES = gettext("View codes");
const GENERATE_NEW_CODES = gettext("Generate new codes");

interface MfaSectionProps {
  title: string;
  status?: ReactNode;
  children: ReactNode;
  testId: string;
}

function MfaSection({ title, status, children, testId }: MfaSectionProps) {
  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${BRAND_COLORS.border}`,
        borderRadius: 2,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
      })}
      data-testid={testId}
    >
      <Stack
        direction="row"
        sx={(theme) => ({
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: theme.spacing(1),
        })}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: BRAND_COLORS.text.primary,
          }}
        >
          {title}
        </Typography>
        {status}
      </Stack>
      {children}
    </Box>
  );
}

export function MfaIndexPage() {
  const {
    totpActive,
    totpActivationBlockedReason,
    isMfaEnabled,
    recoveryCodes,
    messages = [],
  } = useAuthContext();

  // allauth also queues the blocked reason as a message when the user opens the
  // activation URL directly; show it once.
  const blockedReasonShownAsMessage = messages.some(
    (message) => message.message === totpActivationBlockedReason,
  );

  return (
    <AuthLayout title={TITLE} subtitle={SUBTITLE}>
      <MfaContent>
        <MfaMessages />

        <MfaSection
          title={AUTHENTICATOR_APP}
          testId="mfa-totp-section"
          status={
            <Chip
              size="small"
              label={totpActive ? ACTIVE : NOT_ACTIVE}
              color={totpActive ? "success" : "default"}
              data-testid="mfa-totp-status"
            />
          }
        >
          <MfaText>
            {totpActive ? TOTP_ACTIVE_TEXT : TOTP_INACTIVE_TEXT}
          </MfaText>
          {totpActive ? (
            <SecondaryButton
              href={MFA_URLS.deactivateTotp}
              data-testid="mfa-deactivate-totp-button"
            >
              {DEACTIVATE}
            </SecondaryButton>
          ) : totpActivationBlockedReason ? (
            !blockedReasonShownAsMessage && (
              <Alert severity="warning" data-testid="mfa-activation-blocked">
                {totpActivationBlockedReason}
              </Alert>
            )
          ) : (
            <PrimaryButton
              href={MFA_URLS.activateTotp}
              data-testid="mfa-activate-totp-button"
            >
              {ACTIVATE}
            </PrimaryButton>
          )}
        </MfaSection>

        {isMfaEnabled && (
          <MfaSection
            title={RECOVERY_CODES}
            testId="mfa-recovery-codes-section"
          >
            <MfaText>{RECOVERY_CODES_TEXT}</MfaText>
            <MfaText>
              {recoveryCodes
                ? interpolate(RECOVERY_CODES_COUNT, [
                    recoveryCodes.unusedCount,
                    recoveryCodes.totalCount ?? recoveryCodes.unusedCount,
                  ])
                : NO_RECOVERY_CODES}
            </MfaText>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {recoveryCodes && recoveryCodes.unusedCount > 0 && (
                <SecondaryButton
                  href={MFA_URLS.recoveryCodes}
                  data-testid="mfa-view-recovery-codes-button"
                >
                  {VIEW_CODES}
                </SecondaryButton>
              )}
              <SecondaryButton
                href={MFA_URLS.generateRecoveryCodes}
                data-testid="mfa-generate-recovery-codes-button"
              >
                {GENERATE_NEW_CODES}
              </SecondaryButton>
            </Stack>
          </MfaSection>
        )}

        <MfaBackLink to="profile" />
      </MfaContent>
    </AuthLayout>
  );
}
